import { useParams, useNavigate, Link } from 'react-router'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatAmount } from '@/lib/utils'
import { useTags, useTransactionsByTag } from '@/lib/hooks'
import { TagBadge } from '@/components/tag/tag-badge'
import { TagSettlement } from '@/components/tag/tag-settlement'
import { TransactionListItem } from '@/components/transaction/transaction-list-item'

export default function TagTransactionsPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  const { data: tags = [], isLoading: tagsLoading } = useTags()
  const { data: transactions = [], isLoading: txLoading } = useTransactionsByTag(id)

  const currentTag = tags.find(t => t.id === id)
  const totalAmount = transactions.reduce((sum, tx) => sum + tx.total_amount, 0)

  if (tagsLoading || txLoading) {
    return (
      <div className="mx-auto max-w-lg space-y-4 px-4 py-6">
        <div className="flex items-center gap-2">
          <Link to="/tags" replace>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="size-5" />
            </Button>
          </Link>
          <h1 className="text-xl font-semibold">タグ</h1>
        </div>

        {/* Tag chip bar skeleton */}
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 scrollbar-none">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-7 w-24 shrink-0 rounded-full" />
          ))}
        </div>

        {/* Summary skeleton */}
        <Skeleton className="h-11 w-full rounded-lg" />

        {/* Payment ratio card skeleton */}
        <Card>
          <CardHeader>
            <CardTitle>支払い割合</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Payer bar + legend */}
            <div className="space-y-2">
              <Skeleton className="h-2 w-full rounded-full" />
              <ul className="space-y-1">
                {Array.from({ length: 2 }).map((_, i) => (
                  <li
                    key={i}
                    className="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-1 py-1"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <Skeleton className="h-2 w-1 rounded-sm" />
                      <Skeleton className="size-5 rounded-full" />
                      <Skeleton className="h-4 w-20" />
                    </span>
                    <Skeleton className="h-3 w-8" />
                    <Skeleton className="h-4 w-16" />
                  </li>
                ))}
              </ul>
            </div>

            {/* Category bar + legend */}
            <div className="space-y-2 border-t pt-3">
              <Skeleton className="h-2 w-full rounded-full" />
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                {Array.from({ length: 4 }).map((_, i) => (
                  <span key={i} className="flex items-center gap-1.5">
                    <Skeleton className="size-2 rounded-full" />
                    <Skeleton className="h-3 w-16" />
                  </span>
                ))}
              </div>
            </div>

            {/* Total */}
            <div className="flex items-center justify-between border-t pt-2">
              <Skeleton className="h-4 w-10" />
              <Skeleton className="h-4 w-20" />
            </div>
          </CardContent>
        </Card>

        {/* Transaction list skeleton */}
        <div className="divide-y">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between gap-3 px-2 py-3">
              <div className="flex items-center gap-3">
                <Skeleton className="h-4 w-10" />
                <div className="space-y-1">
                  <Skeleton className="h-4 w-28" />
                  <div className="flex items-center gap-1">
                    <Skeleton className="size-2 rounded-full" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="size-5 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-lg space-y-4 px-4 py-6">
      <div className="flex items-center gap-2">
        <Link to="/tags" replace>
          <Button variant="ghost" size="icon">
            <ArrowLeft className="size-5" />
          </Button>
        </Link>
        <h1 className="text-xl font-semibold">タグ</h1>
      </div>

      {/* Tag chip bar */}
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 scrollbar-none">
        {tags.map(tag => (
          <button
            key={tag.id}
            type="button"
            onClick={() => navigate(`/tags/${tag.id}`)}
          >
            <TagBadge name={tag.name} color={tag.color} active={tag.id === id} />
          </button>
        ))}
      </div>

      {/* Summary */}
      {currentTag && (
        <div className="rounded-lg bg-muted px-4 py-3">
          <span className="text-sm text-muted-foreground">
            {transactions.length}件
          </span>
          <span className="mx-2 text-muted-foreground">·</span>
          <span className="text-sm font-medium">
            {formatAmount(totalAmount)}
          </span>
        </div>
      )}

      {/* Payment breakdown & split-bill suggestion */}
      <TagSettlement transactions={transactions} />

      {/* Transaction list */}
      {transactions.length > 0 ? (
        <div className="divide-y">
          {transactions.map(tx => (
            <TransactionListItem
              key={tx.id}
              transaction={tx}
              to={`/transactions/${tx.id}`}
            />
          ))}
        </div>
      ) : (
        <p className="py-16 text-center text-sm text-muted-foreground">
          このタグに紐づく取引はありません
        </p>
      )}
    </div>
  )
}
